import { useSelector } from 'react-redux';

import Avatar from '@components/avatar/Avatar';
import ThreeDotActionButton from '@components/three-dot-action-button/ThreeDotActionButton';
import { FEATURE_FLAGS } from '@constants/feature';
import UserPermissionTypes from '@constants/permissions';
import { PaymentCollectionType } from '@features/group-management/constants';
import { RoleType } from '@features/team-management/constants';
import useCheckPermission from '@hooks/checkPermission';
import { Group } from '@types';

import Icon from '../icon/Icon';
// import ProgressBar from '../progress-bar/ProgressBar';
import Tooltip from '../tooltip/Tooltip';
import getGroupCardColor from './utils';

import theme from '../../../themes/colors';

interface GroupCardProps {
  groupCardId?: string;
  groupDetails: Group;
  className?: string;
  handleGroupCardClick: () => void;
  onClickEdit: (e: React.MouseEvent) => void;
  onClickDuplicate: (e: React.MouseEvent, group: Group) => void;
}

const GroupCard = ({
  groupCardId,
  groupDetails,
  className,
  handleGroupCardClick,
  onClickEdit,
  onClickDuplicate
}: GroupCardProps) => {
  const { checkPermission } = useCheckPermission();
  const { orgFeatureFlags } = useSelector((state: any) => state.user);
  // const received: number = Number(groupDetails.pay_received);
  // const due: number = Number(groupDetails.pay_due);
  // const total: number = received + due;
  // const currentProgress: number =
  //   total === 0 ? 0 : Math.round((received / total) * 100);

  const hasGroupEditAccess = checkPermission([UserPermissionTypes.GROUP_EDIT]);

  const { currentOrg } = useSelector((state: any) => state.user);
  const isAttendanceManager = currentOrg.role === RoleType.ATTENDANCE_MANAGER;

  const showEditGroupIcon =
    hasGroupEditAccess &&
    !(
      groupDetails.interval === PaymentCollectionType.ONCE &&
      !groupDetails.is_active &&
      groupDetails.end_date &&
      new Date(groupDetails.end_date).setHours(0, 0, 0, 0) <
        new Date().setHours(0, 0, 0, 0)
    );

  const renderGroupLogo = () => (
    <Avatar
      name={groupDetails.name}
      imageUrl={groupDetails.image_url}
      className="size-10 rounded-lg"
      bgColor={
        !groupDetails.is_active
          ? theme.cofeeNeutralInactive
          : getGroupCardColor(groupDetails.id)
      }
    />
  );

  const renderGroupName = () => (
    <div className="flex h-12 flex-col justify-between gap-1">
      <div className="truncate break-words text-base font-medium text-cofeeNeutral10">
        {groupDetails.name}
      </div>
      <div className="text-sm font-normal text-cofeeNeutral8">
        {(groupDetails.active_member_count ?? 0) === groupDetails.member_count
          ? `${groupDetails.active_member_count ?? 0} ${
              groupDetails.active_member_count === 1
                ? 'Active Member'
                : 'Active Members'
            }`
          : `${groupDetails.active_member_count ?? 0}/${
              groupDetails.member_count
            } Active Members`}
      </div>
    </div>
  );

  const renderGroupCreationIncompleteWarning = () => (
    <>
      <Icon
        name="warning"
        size="24"
        data-tooltip-id={`incomplete-group-info-${groupDetails.id}`}
      />
      <Tooltip
        id={`incomplete-group-info-${groupDetails.id}`}
        place="bottom"
        className="max-w-67.5"
      >
        <div className="flex flex-col items-center justify-center gap-2 font-normal">
          <div className="text-sm font-medium text-white">
            Group creation incomplete
          </div>
          <div className="text-xs text-grey40">
            Please review and complete all required fields to finish group
            creation.
          </div>
        </div>
      </Tooltip>
    </>
  );

  const getActions = (group: Group) => {
    const actions = [
      {
        id: `edit-${group.id}`,
        label: 'Edit',
        onClick: (e: React.MouseEvent) => onClickEdit(e)
      },
      orgFeatureFlags &&
        orgFeatureFlags.includes(FEATURE_FLAGS.CLONE_GROUP) && {
          id: `duplicate-${group.id}`,
          label: 'Duplicate',
          onClick: (e: React.MouseEvent) => onClickDuplicate(e, group)
        }
    ].filter(Boolean);

    return { actions };
  };

  const hasOnlyEditAction = () => {
    const { actions } = getActions(groupDetails);
    return actions.length === 1 && actions[0].id.startsWith('edit-');
  };

  return (
    <div
      data-testid={groupCardId}
      className={`hover:bg-cofeeHoverBlue relative min-h-33 w-full cursor-pointer rounded-xl border border-cofeeNeutral4 bg-cofeeNeutral1 py-3 ${
        !groupDetails.is_active && 'opacity-60'
      } ${className || ''}`}
      role="presentation"
      onClick={handleGroupCardClick}
    >
      <div className="flex justify-between gap-2 px-3">
        <div className="flex flex-col gap-1.5">
          {renderGroupLogo()}
          {renderGroupName()}
        </div>
        {!groupDetails.amount && renderGroupCreationIncompleteWarning()}
      </div>
      {!isAttendanceManager && (
        <div className="mt-3 flex items-center justify-between border-t border-cofeeNeutral5 px-3 pt-3 text-sm font-normal text-cofeeNeutral8">
          <div className="flex items-center gap-1">
            <div
              className={`size-2 min-w-2 rounded-full ${
                !groupDetails.is_active ? 'bg-cofeeNeutral7' : 'bg-cofeeRed6'
              }`}
            />
            Total Due Amount
          </div>
          <div className="flex items-center text-sm font-normal text-cofeeNeutral10">
            <span className="mr-0.5 font-inter">₹</span>
            {groupDetails.pay_due}
          </div>
        </div>
      )}
      {showEditGroupIcon && (
        <div className="absolute right-3 top-3 flex size-6 items-center justify-center rounded-2xl">
          {hasOnlyEditAction() ? (
            <div
              role="presentation"
              className="p-1.5"
              onClick={(e) => {
                e.stopPropagation();
                onClickEdit(e);
              }}
            >
              <Icon name="edit" size="16" stroke={theme.cofeeNeutral8} />
            </div>
          ) : (
            <ThreeDotActionButton
              id={groupDetails.id ?? ''}
              actions={getActions(groupDetails).actions}
              isDisabled={false}
              source="Events Card"
              modalStyle="!w-35"
            />
          )}
        </div>
      )}
    </div>
  );
};
export default GroupCard;
